(() => {
  if (customElements.get('cl-campus-gallery')) return;

  class ClCampusGallery extends HTMLElement {
    connectedCallback() {
      if (this.hasConnected) return;

      this.hasConnected = true;
      this.galleryContent = this.querySelector('[data-campus-gallery-content]');
      this.emptyState = this.querySelector('[data-campus-gallery-empty]');
      this.thumbnailCarousel = this.querySelector('[data-campus-gallery-thumbnails]');
      this.mainGallery = this.querySelector('[data-campus-gallery-main]');
      this.pagination = this.querySelector('[data-campus-gallery-pagination]');
      this.sourceBlocks = Array.from(this.querySelectorAll('[data-campus-gallery-block]'));
      this.extractBlocks();
      this.thumbnails = Array.from(this.querySelectorAll('[data-campus-gallery-thumbnail]'));
      this.paginationButtons = [];
      this.panels = Array.from(this.querySelectorAll('[data-campus-gallery-panel]'));
      this.renderPagination();
      this.activeIndex = Math.max(
        0,
        this.panels.findIndex((panel) => panel.classList.contains('is-active'))
      );
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
      this.handleClick = this.onClick.bind(this);
      this.handleKeydown = this.onKeydown.bind(this);
      this.handleBlockSelect = this.onBlockSelect.bind(this);
      this.handlePointerDown = this.onPointerDown.bind(this);
      this.handlePointerUp = this.onPointerUp.bind(this);
      this.handlePointerCancel = this.onPointerCancel.bind(this);

      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('shopify:block:select', this.handleBlockSelect);

      if (this.mainGallery) {
        this.mainGallery.addEventListener('pointerdown', this.handlePointerDown);
        this.mainGallery.addEventListener('pointerup', this.handlePointerUp);
        this.mainGallery.addEventListener('pointercancel', this.handlePointerCancel);
      }

      if (this.panels.length === 0) {
        this.galleryContent?.setAttribute('hidden', '');
        this.emptyState?.removeAttribute('hidden');
        return;
      }

      this.galleryContent?.removeAttribute('hidden');
      this.emptyState?.setAttribute('hidden', '');

      this.toggleNavigation(this.thumbnails.length > 1);
      this.updateAccessibleState(this.activeIndex);

      if (this.thumbnailCarousel && this.thumbnails.length > 0) {
        this.waitForSwiper();
      }
    }

    disconnectedCallback() {
      this.hasConnected = false;
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);

      if (this.mainGallery) {
        this.mainGallery.removeEventListener('pointerdown', this.handlePointerDown);
        this.mainGallery.removeEventListener('pointerup', this.handlePointerUp);
        this.mainGallery.removeEventListener('pointercancel', this.handlePointerCancel);
      }

      if (this.swiperWaitTimer) {
        window.clearTimeout(this.swiperWaitTimer);
        this.swiperWaitTimer = null;
      }

      if (this.animationTimer) {
        window.clearTimeout(this.animationTimer);
        this.animationTimer = null;
      }

      if (this.thumbnailSwiper && !this.thumbnailSwiper.destroyed) {
        this.thumbnailSwiper.destroy(true, true);
      }

      this.thumbnailSwiper = null;
      this.swipeGesture = null;
    }

    waitForSwiper(attempt = 0) {
      if (!this.isConnected) return;

      if (typeof window.Swiper === 'function') {
        this.initializeCarousel();
        return;
      }

      if (attempt >= 100) return;

      this.swiperWaitTimer = window.setTimeout(() => {
        this.waitForSwiper(attempt + 1);
      }, 50);
    }

    initializeCarousel() {
      if (this.thumbnailSwiper || !this.thumbnailCarousel) return;

      this.thumbnailSwiper = new window.Swiper(this.thumbnailCarousel, {
        slidesPerView: 2.35,
        slidesPerGroup: 1,
        spaceBetween: 8,
        speed: this.reducedMotion ? 0 : 300,
        allowTouchMove: this.thumbnails.length > 1,
        simulateTouch: true,
        grabCursor: this.thumbnails.length > 1,
        observer: true,
        observeParents: true,
        watchOverflow: true,
        watchSlidesProgress: true,
        breakpoints: {
          480: {
            slidesPerView: 3.35,
            spaceBetween: 10,
          },
          750: {
            slidesPerView: 4.5,
            spaceBetween: 12,
          },
          990: {
            slidesPerView: 6,
            spaceBetween: 14,
          },
        },
      });
    }

    extractBlocks() {
      const thumbnailWrapper = this.thumbnailCarousel?.querySelector('.swiper-wrapper');
      const total = this.sourceBlocks.length;

      this.sourceBlocks.forEach((sourceBlock, index) => {
        sourceBlock.dataset.galleryIndex = String(index);

        const thumbnailSlide = sourceBlock.querySelector('[data-campus-gallery-thumbnail-source]');
        if (thumbnailSlide && thumbnailWrapper) {
          thumbnailSlide.removeAttribute('data-campus-gallery-thumbnail-source');
          thumbnailSlide.dataset.galleryIndex = String(index);
          thumbnailSlide.querySelector('[data-campus-gallery-thumbnail]')?.setAttribute(
            'data-gallery-index',
            String(index)
          );
          thumbnailWrapper.append(thumbnailSlide);
        }

        const panel = sourceBlock.querySelector('[data-campus-gallery-panel-source]');
        if (panel && this.mainGallery) {
          panel.removeAttribute('data-campus-gallery-panel-source');
          panel.setAttribute('data-campus-gallery-panel', '');
          panel.dataset.galleryIndex = String(index);
          panel.setAttribute('aria-label', `${index + 1} de ${total}`);
          panel.classList.toggle('is-active', index === 0);
          this.mainGallery.append(panel);
        }
      });
    }

    renderPagination() {
      if (!this.pagination) return;

      this.pagination.replaceChildren();

      this.sourceBlocks.forEach((_, index) => {
        const button = document.createElement('button');
        button.className = 'cl-campus-gallery__pagination-dot';
        button.type = 'button';
        button.setAttribute('aria-label', `Exibir imagem ${index + 1} de ${this.sourceBlocks.length}`);
        button.setAttribute('aria-controls', this.mainGallery?.id || '');
        button.dataset.campusGalleryPage = '';
        button.dataset.galleryIndex = String(index);
        this.pagination.append(button);
      });

      this.paginationButtons = Array.from(this.pagination.querySelectorAll('[data-campus-gallery-page]'));
    }

    onPointerDown(event) {
      if (!this.mobileMediaQuery.matches || this.panels.length <= 1 || event.isPrimary === false) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.swipeGesture = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
      };

      this.mainGallery.setPointerCapture?.(event.pointerId);
    }

    onPointerUp(event) {
      if (!this.swipeGesture || this.swipeGesture.pointerId !== event.pointerId) return;

      const horizontalDistance = event.clientX - this.swipeGesture.startX;
      const verticalDistance = event.clientY - this.swipeGesture.startY;
      this.swipeGesture = null;

      if (Math.abs(horizontalDistance) < 40 || Math.abs(horizontalDistance) <= Math.abs(verticalDistance)) return;

      this.goToRelative(horizontalDistance < 0 ? 1 : -1);
    }

    onPointerCancel(event) {
      if (this.swipeGesture?.pointerId === event.pointerId) {
        this.swipeGesture = null;
      }
    }

    onClick(event) {
      const thumbnail = event.target.closest('[data-campus-gallery-thumbnail]');
      const paginationButton = event.target.closest('[data-campus-gallery-page]');
      const previousButton = event.target.closest('[data-campus-gallery-previous]');
      const nextButton = event.target.closest('[data-campus-gallery-next]');

      const galleryControl = thumbnail || paginationButton;

      if (galleryControl) {
        event.preventDefault();
        const targetIndex = this.getIndex(galleryControl);
        if (targetIndex !== null) this.goTo(targetIndex);
        return;
      }

      if (previousButton) {
        event.preventDefault();
        this.goToRelative(-1);
        return;
      }

      if (nextButton) {
        event.preventDefault();
        this.goToRelative(1);
      }
    }

    onKeydown(event) {
      const thumbnail = event.target.closest('[data-campus-gallery-thumbnail]');
      if (!thumbnail) return;

      const currentIndex = this.getIndex(thumbnail);
      if (currentIndex === null) return;

      let targetIndex = null;

      if (event.key === 'ArrowLeft') {
        targetIndex = (currentIndex - 1 + this.thumbnails.length) % this.thumbnails.length;
      } else if (event.key === 'ArrowRight') {
        targetIndex = (currentIndex + 1) % this.thumbnails.length;
      } else if (event.key === 'Home') {
        targetIndex = 0;
      } else if (event.key === 'End') {
        targetIndex = this.thumbnails.length - 1;
      }

      if (targetIndex === null) return;

      event.preventDefault();
      const direction = event.key === 'ArrowLeft' ? -1 : 1;
      this.goTo(targetIndex, direction);
      this.thumbnails[targetIndex].focus({ preventScroll: true });
    }

    onBlockSelect(event) {
      const block = event.target.closest('[data-campus-gallery-block]');
      if (!block) return;

      const targetIndex = this.getIndex(block);
      if (targetIndex === null) return;

      this.goTo(targetIndex, null, false);
    }

    getIndex(element) {
      const index = Number.parseInt(element.dataset.galleryIndex, 10);
      return Number.isInteger(index) && index >= 0 && index < this.panels.length ? index : null;
    }

    goToRelative(direction) {
      if (this.panels.length <= 1) return;

      const targetIndex = (this.activeIndex + direction + this.panels.length) % this.panels.length;
      this.goTo(targetIndex, direction);
    }

    goTo(targetIndex, requestedDirection = null, animate = true) {
      if (!Number.isInteger(targetIndex) || targetIndex < 0 || targetIndex >= this.panels.length) return;

      const previousIndex = this.activeIndex;
      const direction = requestedDirection || (targetIndex >= previousIndex ? 1 : -1);

      if (targetIndex !== previousIndex) {
        this.transitionPanels(previousIndex, targetIndex, direction, animate);
        this.activeIndex = targetIndex;
        this.updateAccessibleState(targetIndex);
      }

      this.keepThumbnailVisible(targetIndex);
    }

    transitionPanels(previousIndex, targetIndex, direction, animate) {
      const previousPanel = this.panels[previousIndex];
      const targetPanel = this.panels[targetIndex];
      if (!previousPanel || !targetPanel) return;

      if (this.animationTimer) {
        window.clearTimeout(this.animationTimer);
        this.animationTimer = null;
      }

      this.panels.forEach((panel) => {
        panel.classList.remove(
          'is-entering',
          'is-entering--next',
          'is-entering--previous',
          'is-leaving',
          'is-leaving--next',
          'is-leaving--previous'
        );

        if (panel !== previousPanel) panel.classList.remove('is-active');
      });

      if (!animate || this.reducedMotion) {
        previousPanel.classList.remove('is-active');
        targetPanel.classList.add('is-active');
        return;
      }

      const directionName = direction > 0 ? 'next' : 'previous';

      targetPanel.classList.add('is-entering', `is-entering--${directionName}`);
      targetPanel.getBoundingClientRect();

      previousPanel.classList.add('is-leaving', `is-leaving--${directionName}`);
      previousPanel.classList.remove('is-active');
      targetPanel.classList.add('is-active');
      targetPanel.classList.remove('is-entering', `is-entering--${directionName}`);

      this.animationTimer = window.setTimeout(() => {
        previousPanel.classList.remove('is-leaving', `is-leaving--${directionName}`);
        this.animationTimer = null;
      }, 340);
    }

    updateAccessibleState(activeIndex) {
      this.thumbnails.forEach((thumbnail, index) => {
        const isActive = index === activeIndex;
        thumbnail.classList.toggle('is-active', isActive);

        if (isActive) {
          thumbnail.setAttribute('aria-current', 'true');
        } else {
          thumbnail.removeAttribute('aria-current');
        }
      });

      this.paginationButtons.forEach((button, index) => {
        const isActive = index === activeIndex;
        button.classList.toggle('is-active', isActive);

        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });

      this.panels.forEach((panel, index) => {
        panel.setAttribute('aria-hidden', String(index !== activeIndex));
      });
    }

    keepThumbnailVisible(index) {
      if (!this.thumbnailSwiper || this.thumbnailSwiper.destroyed) return;

      this.thumbnailSwiper.updateSlidesProgress();
      const targetSlide = this.thumbnailSwiper.slides[index];
      if (!targetSlide || targetSlide.classList.contains('swiper-slide-visible')) return;

      this.thumbnailSwiper.slideTo(index, this.reducedMotion ? 0 : 300);
    }

    toggleNavigation(enabled) {
      this.querySelectorAll('[data-campus-gallery-previous], [data-campus-gallery-next]').forEach((button) => {
        button.disabled = !enabled;
        button.setAttribute('aria-disabled', String(!enabled));
      });
    }
  }

  customElements.define('cl-campus-gallery', ClCampusGallery);
})();
